import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Phone,
  MapPin,
  Package,
  Plus,
  Trash2,
  X,
  AlertCircle,
  Edit,
  TrendingUp,
  Calendar,
  CreditCard
} from 'lucide-react';
import { customersAPI } from '../utils/api';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { displayUpperCase } from '../utils/textUtils';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useFormDraft } from '../hooks/useFormDraft';
import { supabaseWithRetry, getUserFriendlyErrorMessage } from '../utils/retryApi';

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError, showWarning } = useNotification();
  const { isWarehouse } = useAuth();

  // State management
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    return ['overview', 'products', 'summary'].includes(tabFromUrl) ? tabFromUrl : 'overview';
  });


  // Product operations
  const [showProductModal, setShowProductModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOperation, setEditingOperation] = useState(null);
  const [productOperations, setProductOperations] = useState([]);
  const [productFormData, setProductFormData] = useState({
    brand: '',
    productName: '',
    model: '',
    serialNumber: '',
    notes: ''
  });
  const [productSubmitting, setProductSubmitting] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Balance transactions for summary
  const [balanceTransactions, setBalanceTransactions] = useState([]);

  // Network status hook
  const { isOnline } = useOnlineStatus();

  // Form draft for product operations
  const { draft: productDraft, updateDraft: updateProductDraft, clearDraft: clearProductDraft } = useFormDraft(
    `product:${id}`,
    {
      brand: '',
      productName: '',
      model: '',
      serialNumber: '',
      notes: ''
    },
    { autoSave: true, saveDelay: 500 }
  );


  // Handle tab change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };


  // Fetch customer details
  const fetchCustomer = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await customersAPI.getCustomer(id);
      setCustomer(response.data.customer);
    } catch (err) {
      console.error('Error fetching customer:', err);
      setError('Müşteri bilgileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch product operations
  const fetchProductOperations = useCallback(async () => {
    try {
      const response = await customersAPI.getProductOperations(id, { limit: 100 });
      setProductOperations(response.data.operations);
    } catch (err) {
      console.error('Error fetching product operations:', err);
      setProductOperations([]);
    }
  }, [id]);

  // Fetch balance transactions for summary (only if not warehouse user)
  const fetchBalanceTransactions = useCallback(async () => {
    if (isWarehouse()) return;

    try {
      const response = await customersAPI.getBalanceTransactions(id, { limit: 50 });
      setBalanceTransactions(response.data.transactions);
    } catch (err) {
      console.error('Error fetching balance transactions:', err);
      setBalanceTransactions([]);
    }
  }, [id, isWarehouse]);

  // Reset pagination when product operations change
  useEffect(() => {
    setCurrentPage(1);
  }, [productOperations]);

  // Pagination helper functions
  const totalPages = Math.ceil(productOperations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentOperations = productOperations.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of table
    const tableElement = document.getElementById('products-table');
    if (tableElement) {
      tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  // Sync active tab with URL changes
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && ['overview', 'products', 'summary'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Load data on component mount
  useEffect(() => {
    fetchCustomer();
    fetchProductOperations();
    fetchBalanceTransactions();
  }, [fetchCustomer, fetchProductOperations, fetchBalanceTransactions]);


  // Handle product operation submission - İyileştirilmiş versiyon
  const handleProductOperationSubmit = async (e) => {
    e.preventDefault();

    // Online/offline kontrolü
    if (!isOnline) {
      showWarning('İnternet bağlantısı yok. Lütfen bağlantınızı kontrol edin ve tekrar deneyin.');
      return;
    }

    console.log('Form submitted with data:', productFormData);
    console.log('Customer ID:', id);

    // Draft'tan veri al
    const currentData = {
      brand: productDraft.brand || productFormData.brand,
      productName: productDraft.productName || productFormData.productName,
      model: productDraft.model || productFormData.model,
      serialNumber: productDraft.serialNumber || productFormData.serialNumber,
      notes: productDraft.notes || productFormData.notes
    };

    if (!currentData.brand || !currentData.productName) {
      showWarning('Marka ve ürün adı alanları zorunludur');
      return;
    }

    try {
      setProductSubmitting(true);

      if (editingOperation) {
        // Güncelleme
        console.log('Updating operation:', editingOperation.id);
        await supabaseWithRetry(
          () => customersAPI.updateProductOperation(editingOperation.id, currentData),
          {
            maxRetries: 3,
            initialDelay: 1000,
            backoffMultiplier: 2
          }
        );
        showSuccess('Ürün işlemi başarıyla güncellendi');
        setShowEditModal(false);
      } else {
        // Yeni ekleme
        console.log('Adding new operation for customer:', id);
        await supabaseWithRetry(
          () => customersAPI.addProductOperation(id, currentData),
          {
            maxRetries: 3,
            initialDelay: 1000,
            backoffMultiplier: 2
          }
        );
        showSuccess('Ürün işlemi başarıyla eklendi');
        setShowProductModal(false);
      }

      // Reset form and close modals
      setProductFormData({
        brand: '',
        productName: '',
        model: '',
        serialNumber: '',
        notes: ''
      });
      setEditingOperation(null);

      // Clear draft on success
      clearProductDraft();

      // Refresh product operations and customer data
      fetchProductOperations();
      fetchCustomer();
    } catch (err) {
      console.error('Product operation error:', err);

      const errorMessage = getUserFriendlyErrorMessage(err);
      showError(`Ürün işlemi işlenemedi: ${errorMessage}`);

      // İnternet hatası durumunda draft'ı koru
      if (!isOnline || err.code === 'NETWORK_ERROR') {
        showWarning('Verileriniz yerel olarak kaydedildi. İnternet bağlantınız geri geldiğinde otomatik olarak gönderilecek.');
      }
    } finally {
      setProductSubmitting(false);
    }
  };


  // Handle product form input change - Draft destekli
  const handleProductFormChange = (e) => {
    const { name, value } = e.target;

    // Update form data
    setProductFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Update draft immediately
    updateProductDraft({ [name]: value });
  };

  // Handle edit operation
  const handleEditOperation = (operation) => {
    setEditingOperation(operation);
    setProductFormData({
      brand: operation.brand,
      productName: operation.productName,
      model: operation.model || '',
      serialNumber: operation.serialNumber || '',
      notes: operation.notes || ''
    });
    setShowEditModal(true);
  };

  // Handle delete operation
  const handleDeleteOperation = async (operationId) => {
    if (window.confirm('Bu işlemi silmek istediğinizden emin misiniz? Bu işlem ve ilgili bakiye işlemleri geri alınamaz.')) {
      try {
        setProductSubmitting(true);
        await customersAPI.deleteProductOperation(operationId);
        showSuccess('İşlem ve ilgili bakiye işlemleri başarıyla silindi');
        fetchProductOperations();

        // If current page becomes empty after deletion, go to previous page
        const remainingItems = productOperations.length - 1;
        const remainingPages = Math.ceil(remainingItems / itemsPerPage);
        if (currentPage > remainingPages && remainingPages > 0) {
          setCurrentPage(remainingPages);
        } else if (remainingPages === 0) {
          setCurrentPage(1);
        }

        // Refresh balance transactions for summary page
        if (activeTab === 'summary') {
          fetchBalanceTransactions();
        }
      } catch (err) {
        console.error('Delete operation error:', err);
        showError('İşlem silinemedi: ' + (err.message || 'Bilinmeyen hata'));
      } finally {
        setProductSubmitting(false);
      }
    }
  };

  // Handle modal close
  const handleCloseModals = () => {
    setShowProductModal(false);
    setShowEditModal(false);
    setEditingOperation(null);

    // Sadece boş değilse draft'tan yükle
    const hasDraftData = productDraft.brand || productDraft.productName || productDraft.model || productDraft.serialNumber || productDraft.notes;

    if (hasDraftData) {
      setProductFormData({
        brand: productDraft.brand || '',
        productName: productDraft.productName || '',
        model: productDraft.model || '',
        serialNumber: productDraft.serialNumber || '',
        notes: productDraft.notes || ''
      });
    } else {
      setProductFormData({
        brand: '',
        productName: '',
        model: '',
        serialNumber: '',
        notes: ''
      });
    }
  };

  // Handle product modal open with draft data
  const handleShowProductModal = () => {
    // Draft'tan veri yükle
    const draftData = {
      brand: productDraft.brand || '',
      productName: productDraft.productName || '',
      model: productDraft.model || '',
      serialNumber: productDraft.serialNumber || '',
      notes: productDraft.notes || ''
    };

    setProductFormData(draftData);
    setShowProductModal(true);
  };

  // Handle edit modal open with draft data

  // Handle product name double click to open operations page
  const handleProductNameDoubleClick = (operation) => {
    // Navigate to product operations page
    navigate(`/customers/${id}/products/${operation.id}`);
  };

  if (loading) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Müşteri bilgileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Hata</h2>
          <p className="text-gray-600 mb-4">{error || 'Müşteri bulunamadı'}</p>
          <button
            onClick={() => navigate('/customers')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Müşteri Listesine Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/customers')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{displayUpperCase(customer.fullName)}</h1>
              <p className="text-sm text-gray-500">MÜŞTERİ DETAYLARI</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200 px-6">
        <nav className="flex space-x-8">
          {[
            { id: 'overview', label: 'Müşteri Bilgisi', icon: User },
            { id: 'products', label: 'Ürün İşlemleri', icon: Package },
            { id: 'summary', label: 'Genel Özet', icon: TrendingUp }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'overview' && (
          <div className="max-w-6xl mx-auto space-y-8">
            {/* Customer Header Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-6">
                  <div className="p-4 bg-gray-50 rounded-full">
                    <User className="h-12 w-12 text-gray-600" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{displayUpperCase(customer.fullName)}</h1>
                    <div className="flex items-center space-x-6 text-gray-600">
                      {customer.tcNumber && (
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium">TC:</span>
                          <span className="font-mono text-gray-800">{displayUpperCase(customer.tcNumber)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Information Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Contact Information */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-900">İletişim Bilgileri</h3>
                  </div>
                </div>
                <div className="p-6 space-y-6">
                  {customer.phone && (
                    <div className="flex items-center space-x-4">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <Phone className="h-5 w-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">TELEFON</p>
                        <p className="font-semibold text-gray-900 text-lg">{displayUpperCase(customer.phone)}</p>
                      </div>
                    </div>
                  )}
                  {customer.email && (
                    <div className="flex items-center space-x-4">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">E-POSTA</p>
                        <p className="font-semibold text-gray-900 text-lg">{displayUpperCase(customer.email)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Address Information */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Adres Bilgileri</h3>
                  </div>
                </div>
                <div className="p-6 space-y-6">
                  {customer.address && (
                    <div className="flex items-start space-x-4">
                      <div className="p-2 bg-gray-100 rounded-lg mt-1">
                        <MapPin className="h-5 w-5 text-gray-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-500">ADRES</p>
                        <p className="font-semibold text-gray-900 text-lg leading-relaxed">{displayUpperCase(customer.address)}</p>
                      </div>
                    </div>
                  )}
                  {(customer.city || customer.district) && (
                    <div className="flex items-center space-x-4">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">KONUM</p>
                        <p className="font-semibold text-gray-900 text-lg">
                          {displayUpperCase([customer.district, customer.city].filter(Boolean).join(', '))}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 gap-6">


              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">KAYIT TARİHİ</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {new Date(customer.createdAt).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-100 rounded-full">
                    <svg className="h-6 w-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}


        {activeTab === 'products' && (
          <div className="w-full space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Ürün İşlemleri</h3>
                  <button
                    onClick={handleShowProductModal}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Yeni Ürün</span>
                  </button>
                </div>
              </div>

              {/* Compact Table */}
              <div id="products-table" className="overflow-x-auto">
                {productOperations.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="h-8 w-8 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">Henüz ürün işlemi bulunmuyor</p>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200 text-base">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider w-28">
                          Tarih
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider w-24">
                          Marka
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider w-20">
                          Model
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                          Ürün
                        </th>
                        <th className="px-6 py-3 text-center text-sm font-medium text-gray-500 uppercase tracking-wider w-20">
                          İşlemler
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentOperations.map((operation, index) => (
                        <tr
                          key={operation.id || index}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                          onDoubleClick={() => handleProductNameDoubleClick(operation)}
                          title="Çift tıklayarak işlemler sayfasını açın"
                        >
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                            {new Date(operation.date).toLocaleDateString('tr-TR')}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900 truncate">
                            {displayUpperCase(operation.brand)}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 truncate">
                            {displayUpperCase(operation.model) || '-'}
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-900">
                            <div className="max-w-xs">
                              <div className="font-medium truncate">{displayUpperCase(operation.productName)}</div>
                              {operation.serialNumber && (
                                <div className="text-gray-500 text-sm truncate">SN: {displayUpperCase(operation.serialNumber)}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditOperation(operation);
                                }}
                                className="inline-flex items-center p-1.5 border border-transparent text-sm font-medium rounded text-amber-700 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-1 focus:ring-amber-500 transition-colors"
                                title="Düzenle"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteOperation(operation.id);
                                }}
                                disabled={productSubmitting}
                                className="inline-flex items-center p-1.5 border border-transparent text-sm font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-1 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Sil"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="border-t border-gray-200 bg-white px-4 py-3 flex items-center justify-between">
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">{startIndex + 1}</span> - <span className="font-medium">{Math.min(endIndex, productOperations.length)}</span> arası,
                        toplam <span className="font-medium">{productOperations.length}</span> ürün
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-200 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Önceki</span>
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>

                        {getPageNumbers().map((page) => (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === page
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                              }`}
                          >
                            {page}
                          </button>
                        ))}

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-200 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Sonraki</span>
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>

                  {/* Mobile pagination - simplified */}
                  <div className="sm:hidden flex items-center justify-between w-full">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
                    >
                      Önceki
                    </button>
                    <span className="text-sm text-gray-700">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
                    >
                      Sonraki
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}


        {activeTab === 'summary' && (
          <div className="max-w-6xl mx-auto space-y-6">
            {/* Genel Özet Kartları */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Package className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">TOPLAM ÜRÜN</p>
                    <p className="text-2xl font-bold text-gray-900">{productOperations.length}</p>
                  </div>
                </div>
              </div>


              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Calendar className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">BU AY İŞLEM</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {productOperations.filter(op => {
                        const opDate = new Date(op.date);
                        const now = new Date();
                        return opDate.getMonth() === now.getMonth() && opDate.getFullYear() === now.getFullYear();
                      }).length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">TOPLAM İŞLEM</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {productOperations.reduce((total, op) => total + (op.details ? op.details.length : 0), 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Son İşlemler Timeline - Hide balance transactions for warehouse users */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">SON İŞLEMLER</h3>
              <div className="space-y-4">
                {(() => {
                  // Get product operation details (only operations, not product additions)
                  const productOperationDetails = productOperations.flatMap(op =>
                    op.details ? op.details.map(detail => ({
                      ...detail,
                      productInfo: {
                        brand: op.brand,
                        productName: op.productName,
                        model: op.model
                      },
                      type: 'product_operation',
                      sortDate: detail.created_at || detail.updated_at || op.created_at
                    })) : []
                  );

                  // Get balance transactions (only if not warehouse user)
                  const balanceTransactionsFormatted = !isWarehouse() ? balanceTransactions.map(transaction => ({
                    ...transaction,
                    type: 'balance_transaction',
                    sortDate: transaction.created_at || transaction.transaction_date
                  })) : [];

                  // Combine and sort by creation date (newest first)
                  const allTransactions = [...productOperationDetails, ...balanceTransactionsFormatted]
                    .sort((a, b) => {
                      const dateA = new Date(a.sortDate);
                      const dateB = new Date(b.sortDate);
                      return dateB - dateA; // Newest first
                    })
                    .slice(0, 10);

                  return allTransactions.map((item, index) => {
                    // Skip balance transactions entirely for warehouse users
                    if (isWarehouse() && item.type === 'balance_transaction') {
                      return null;
                    }

                    return (
                      <div key={`${item.type}-${item.id || index}`} className={`flex items-center space-x-4 p-4 rounded-lg ${item.type === 'product_operation' ? 'bg-blue-50' : 'bg-green-50'
                        }`}>
                        <div className={`p-2 rounded-full ${item.type === 'product_operation' ? 'bg-blue-100' : 'bg-green-100'
                          }`}>
                          {item.type === 'product_operation' ? (
                            <Package className="h-4 w-4 text-blue-600" />
                          ) : (
                            <CreditCard className="h-4 w-4 text-green-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className={`font-medium ${item.type === 'product_operation'
                            ? 'text-gray-900'
                            : 'text-gray-900'
                            }`}>
                            {item.type === 'product_operation'
                              ? displayUpperCase(`${item.productInfo.brand} ${item.productInfo.productName} ${item.productInfo.model || ''}`.trim())
                              : item.notes ? displayUpperCase(item.notes.replace(/^Bakiye\s+/, '')) : 'BAKİYE İŞLEMİ'
                            }
                          </p>
                          <p className="text-sm text-gray-500">
                            {item.type === 'product_operation'
                              ? displayUpperCase(item.operationType) || 'ÜRÜN İŞLEMİ'
                              : displayUpperCase(item.description) || 'BAKİYE İŞLEMİ'
                            }
                          </p>
                          <p className="text-xs text-gray-400">
                            {new Date(item.sortDate).toLocaleDateString('tr-TR')} -
                            {new Date(item.sortDate).toLocaleTimeString('tr-TR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <div className="text-right">
                          {item.type === 'balance_transaction' && item.amount ? (
                            <div>
                              <p className="text-sm font-medium text-green-600">
                                +{parseFloat(item.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                              </p>
                              <p className="text-xs text-gray-500">BAKİYE İŞLEMİ</p>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">
                              {item.type === 'product_operation' ? 'ÜRÜN İŞLEMİ' : 'BAKİYE İŞLEMİ'}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  }).filter(Boolean); // Filter out null values
                })()}
              </div>
            </div>
          </div>
        )}

      </div>


      {/* Product Operation Modal */}
      {(showProductModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingOperation ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}
                </h2>
                <button
                  onClick={handleCloseModals}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <form onSubmit={handleProductOperationSubmit} className="space-y-6">
                {/* Ürün Bilgileri */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Ürün Bilgileri</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Marka <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="brand"
                        value={productFormData.brand}
                        onChange={handleProductFormChange}
                        required
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Ürün markası"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ürün Adı <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="productName"
                        value={productFormData.productName}
                        onChange={handleProductFormChange}
                        required
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Ürün adı"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                      <input
                        type="text"
                        name="model"
                        value={productFormData.model}
                        onChange={handleProductFormChange}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Ürün modeli"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Seri No</label>
                      <input
                        type="text"
                        name="serialNumber"
                        value={productFormData.serialNumber}
                        onChange={handleProductFormChange}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Seri numarası"
                      />
                    </div>
                  </div>
                </div>

                {/* Notlar */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Notlar</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ürün Notları</label>
                    <textarea
                      name="notes"
                      value={productFormData.notes}
                      onChange={handleProductFormChange}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder="Ürün hakkında notlar (opsiyonel)"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleCloseModals}
                    className="px-6 py-2.5 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={productSubmitting}
                    className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {productSubmitting ? 'İşleniyor...' : (editingOperation ? 'Güncelle' : 'Kaydet')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDetails;
